import { useCallback, useState } from "react";

export interface WeatherLocation {
    lat: number;
    lng: number;
    /** null after a geolocation fix — the weather response supplies the place name. */
    label: string | null;
}

const STORAGE_KEY = "weather_location";

/** Arctic Spas' home city. Shown until the dealer picks somewhere or shares their location. */
export const DEFAULT_LOCATION: WeatherLocation = {
    lat: 53.5461,
    lng: -113.4938,
    label: "Edmonton",
};

const readStored = (): WeatherLocation | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);

        // Guard against a hand-edited or half-written entry putting NaN into the query string.
        if (typeof parsed?.lat !== "number" || typeof parsed?.lng !== "number") return null;

        return { lat: parsed.lat, lng: parsed.lng, label: parsed.label ?? null };
    } catch {
        return null;
    }
};

/**
 * The weather card's location, in priority order: the dealer's saved choice, else Edmonton.
 *
 * Browser geolocation is never requested on mount — only from requestPrecise(), which the card
 * wires to an explicit button — so opening the dashboard never triggers a permission prompt.
 */
const useWeatherLocation = () => {
    const [location, setLocationState] = useState<WeatherLocation>(
        () => readStored() ?? DEFAULT_LOCATION,
    );
    const [locating, setLocating] = useState(false);
    const [geoError, setGeoError] = useState<string | null>(null);

    const setLocation = useCallback((next: WeatherLocation) => {
        setLocationState(next);
        setGeoError(null);

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
            // Private browsing or a full quota — the choice just will not survive a reload.
        }
    }, []);

    const requestPrecise = useCallback(() => {
        if (!navigator.geolocation) {
            setGeoError("Location is not available in this browser.");
            return;
        }

        setLocating(true);
        setGeoError(null);

        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                setLocating(false);
                // Label is left null deliberately: the weather response names the place, which
                // avoids a second round trip just to reverse-geocode.
                setLocation({ lat: coords.latitude, lng: coords.longitude, label: null });
            },
            (error) => {
                setLocating(false);
                setGeoError(
                    error.code === error.PERMISSION_DENIED
                        ? "Location permission denied. Search for a city instead."
                        : "Could not get your location. Search for a city instead.",
                );
            },
            { timeout: 10000, maximumAge: 300000 },
        );
    }, [setLocation]);

    const dismissError = useCallback(() => setGeoError(null), []);

    return { location, setLocation, requestPrecise, locating, geoError, dismissError };
};

export default useWeatherLocation;
